import { useCallback, useEffect, useState } from "react";
import { BarChart3, ShoppingBag, Users, Wallet } from "lucide-react";
import { useAuth } from "../../services/AuthContext";
import { getAdminSales, getAdminStats, getAdminStatsSeries } from "../../services/admin";
import {
  defaultAdminDateRange,
  formatBRL,
  formatDateTimeBr,
  formatInteger,
  SUBSCRIPTION_STATUS_LABELS,
} from "../../config/admin";
import DateRangeBar from "./DateRangeBar";
import AdminPagination from "./AdminPagination";
import AdminReloadOverlay from "./AdminReloadOverlay";
import AdminStatsChart from "./AdminStatsChart";

const PAGE_SIZE = 20;

const PAYMENT_LABELS = {
  RECEIVED: "Recebido",
  CONFIRMED: "Confirmado",
  PENDING: "Pendente",
  OVERDUE: "Vencido",
};

const CYCLE_LABELS = {
  MONTHLY: "Mensal",
  YEARLY: "Anual",
  QUINQUENNIAL: "Quinquenal",
};

const BILLING_LABELS = {
  PIX: "PIX",
  CREDIT_CARD: "Cartão",
};

const statusClass = (status) => {
  const key = String(status || "").toLowerCase();
  return `admin-status admin-status-${key}`;
};

export default function AdminStatsPage() {
  const { getIdToken } = useAuth();
  const initialRange = defaultAdminDateRange(30);
  const [range, setRange] = useState(initialRange);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [page, setPage] = useState({ offset: 0, total: 0, has_next: false, has_prev: false });
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [series, setSeries] = useState({ grain: "day", points: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadData = useCallback(
    async (offset = 0, rangeOverride = null) => {
      const rangeToUse = rangeOverride || appliedRange;
      try {
        setLoading(true);
        setError("");
        const token = await getIdToken();
        const [statsData, salesData, seriesData] = await Promise.all([
          getAdminStats(token, rangeToUse),
          getAdminSales(token, {
            ...rangeToUse,
            offset,
            limit: PAGE_SIZE,
            q: query || undefined,
            subscriptionStatus: subscriptionStatus || undefined,
            paymentStatus: paymentStatus || undefined,
          }),
          getAdminStatsSeries(token, rangeToUse).catch(() => ({
            grain: "day",
            points: [],
          })),
        ]);
        setStats(statsData);
        setSeries({
          grain: seriesData.grain || "day",
          points: seriesData.points || [],
        });
        setSales(salesData.sales || []);
        setPage({
          offset: salesData.offset ?? offset,
          total: salesData.total ?? 0,
          has_next: Boolean(salesData.has_next),
          has_prev: Boolean(salesData.has_prev),
        });
      } catch (err) {
        setError(err.message || "Não foi possível carregar as estatísticas.");
      } finally {
        setLoading(false);
      }
    },
    [appliedRange, getIdToken, query, subscriptionStatus, paymentStatus]
  );

  useEffect(() => {
    loadData(0);
  }, [loadData]);

  return (
    <div className="admin-page">
      <AdminReloadOverlay visible={loading} message="Atualizando estatísticas..." />
      <div className="admin-page-header">
        <h1 className="admin-title">
          <BarChart3 size={30} className="admin-title-icon" />
          Estatísticas
        </h1>
        <p className="admin-subtitle">
          Números do site no período selecionado (horário de Brasília).
        </p>
      </div>

      <DateRangeBar
        startDate={range.startDate}
        endDate={range.endDate}
        onChange={setRange}
        onApply={(next) => {
          setAppliedRange(next);
          setRange(next);
        }}
        onReload={(next) => {
          setRange(next);
          if (
            next.startDate === appliedRange.startDate &&
            next.endDate === appliedRange.endDate
          ) {
            loadData(page.offset, next);
          } else {
            setAppliedRange(next);
          }
        }}
        loading={loading}
      />

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <p className="admin-kpi-label">
            <ShoppingBag size={16} /> Novas vendas
          </p>
          <p className="admin-kpi-value">{formatInteger(stats?.new_sales)}</p>
        </div>
        <div className="admin-kpi-card">
          <p className="admin-kpi-label">
            <Users size={16} /> Usuários ativos
          </p>
          <p className="admin-kpi-value">{formatInteger(stats?.users_active)}</p>
        </div>
        <div className="admin-kpi-card">
          <p className="admin-kpi-label">
            <Wallet size={16} /> Receita bruta
          </p>
          <p className="admin-kpi-value">{formatBRL(stats?.gross_revenue)}</p>
        </div>
      </div>

      <div className="admin-panel">
        <h2 className="admin-panel-title">Receita e vendas no tempo</h2>
        <p className="admin-muted" style={{ marginBottom: 12 }}>
          Vendas confirmadas ou recebidas no período selecionado.
        </p>
        <AdminStatsChart points={series.points} grain={series.grain} />
      </div>

      <div className="admin-panel">
        <h2 className="admin-panel-title">Vendas do período</h2>
        <div className="admin-controls" style={{ marginBottom: 16 }}>
          <div className="admin-control admin-control-grow">
            <label className="admin-control-label" htmlFor="admin-sales-search">
              Buscar venda
            </label>
            <input
              id="admin-sales-search"
              type="search"
              className="admin-control-input"
              placeholder="Nome, e-mail ou ID do pagamento..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <div className="admin-control admin-control-status">
            <label className="admin-control-label" htmlFor="admin-sales-payment-status">
              Status do pagamento
            </label>
            <select
              id="admin-sales-payment-status"
              className="admin-control-select"
              value={paymentStatus}
              onChange={(event) => setPaymentStatus(event.target.value)}
            >
              <option value="">Todos</option>
              {Object.entries(PAYMENT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-control admin-control-status">
            <label className="admin-control-label" htmlFor="admin-sales-subscription-status">
              Status da assinatura
            </label>
            <select
              id="admin-sales-subscription-status"
              className="admin-control-select"
              value={subscriptionStatus}
              onChange={(event) => setSubscriptionStatus(event.target.value)}
            >
              <option value="">Todos</option>
              {Object.entries(SUBSCRIPTION_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {sales.length === 0 && !loading && (
          <div className="admin-empty">Nenhuma venda encontrada neste período.</div>
        )}

        {sales.length > 0 && (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Aluno</th>
                    <th>Valor</th>
                    <th>Pagamento</th>
                    <th>Assinatura</th>
                    <th>Oferta</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.payment_id}>
                      <td>{formatDateTimeBr(sale.payment_created_at)}</td>
                      <td>
                        <strong>{sale.full_name || "—"}</strong>
                        <div className="admin-muted">{sale.email || "—"}</div>
                      </td>
                      <td>{formatBRL(sale.value)}</td>
                      <td>
                        <span className={statusClass(sale.payment_status)}>
                          {PAYMENT_LABELS[sale.payment_status] || sale.payment_status}
                        </span>
                        <div className="admin-muted">
                          {BILLING_LABELS[sale.billing_type] || sale.billing_type || "—"}
                          {sale.num_insts > 1 ? ` · ${sale.num_insts}x` : ""}
                        </div>
                      </td>
                      <td>
                        {CYCLE_LABELS[sale.cycle] || sale.cycle || "—"}
                        <div className="admin-muted">
                          {SUBSCRIPTION_STATUS_LABELS[sale.subscription_status] ||
                            sale.subscription_status ||
                            "—"}
                        </div>
                      </td>
                      <td>
                        {sale.offer_name || sale.offer_code || "—"}
                        <div className="admin-muted">{sale.payment_external_id || "—"}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination
              offset={page.offset}
              limit={PAGE_SIZE}
              total={page.total}
              itemLabel="venda"
              visibleCount={sales.length}
              disabled={loading}
              onPage={(nextOffset) => loadData(nextOffset)}
            />
          </>
        )}
      </div>
    </div>
  );
}
